<template>
  <generic-error
    :title="errorDetails.title"
    :buttonValue="errorDetails.buttonValue"
    :buttonHref="errorDetails.buttonHref"
    :buttonTo="errorDetails.buttonTo"
    layout="centered"
  >
    <!-- eslint-disable-next-line vue/no-v-html -->
    <span v-html="errorDetails.body" />
  </generic-error>
</template>

<script lang="ts">
import GenericError from '@/components/generic_error/GenericError.vue';
import getNewProfileRoute from '@/helpers/profileRouteHelper';

export default {
  name: 'ProviderProfileError',
  components: {
    GenericError,
  },
  props: {
    errorType: {
      type: String,
      default: '',
    },
    viewerAccountData: {
      type: Object,
      default: () => ({}),
    },
    paramData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    viewerLoggedIn(): boolean {
      return this.viewerAccountData !== null;
    },
    errorDetails(): { title: string; body: string; buttonValue?: string; buttonHref?: string; buttonTo?: string | object } {
      switch (this.errorType) {
        case 'PROFILE_UNAVAILABLE':
          return {
            ...this.unavailableErrorData,
          };
        case 'PROFILE_INCOMPLETE':
          return {
            ...this.incompleteErrorData,
          };
        default:
          return {
            title: 'This profile is unavailable',
            body: 'There was an error with processing your request. Refresh the page to try again or contact us if the issue continues.',
            buttonValue: 'Contact Customer Success',
            buttonHref: 'https://support.sittercity.com/hc/en-us/requests/new',
          };
      }
    },
    incompleteErrorData(): {
      title: string;
      body: string;
      buttonValue?: string;
      buttonHref?: string;
      buttonTo?: string | object;
    } {
      const { careType } = this.paramData;
      const careTypeDisplay = careType.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      return {
        title: 'This profile is incomplete',
        body: `You do not have a completed ${careTypeDisplay} Profile. Complete it to start applying for ${careTypeDisplay.toLowerCase()} jobs.`,
        buttonValue: `Complete your ${careTypeDisplay} Profile`,
        buttonTo: getNewProfileRoute(careType),
      };
    },
    unavailableErrorData(): {
      title: string;
      body: string;
      buttonValue?: string;
      buttonHref?: string;
      buttonTo?: string | object;
    } {
      const baseMessage = 'It may have been removed or is no longer available.';
      const { careType, sitterUuid } = this.paramData;
      const isProfileOwner = this.viewerLoggedIn && this.viewerAccountData?.id === sitterUuid;
      const isSitter = this.viewerAccountData?.isSitter?.() || false;

      if (isProfileOwner) {
        const careTypeDisplay = careType.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        return {
          title: 'This profile is unavailable',
          body: `You do not have a ${careTypeDisplay} Profile. Create one to start applying for ${careTypeDisplay} jobs.`,
          buttonValue: `Create ${careTypeDisplay} Profile`,
          buttonTo: getNewProfileRoute(careType),
        };
      }

      if (this.viewerLoggedIn && isSitter) {
        const careTypeDisplay = careType.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        return {
          title: 'This profile is unavailable',
          body: `${baseMessage} You can search for ${careTypeDisplay} jobs on Sittercity.`,
          buttonValue: `Find ${careTypeDisplay} Jobs`,
          buttonTo: { name: 'JobSearch', query: { careType } },
        };
      }

      if (this.viewerLoggedIn) {
        return {
          title: 'This profile is unavailable',
          body: `${baseMessage} Return <a class="text-primary primary-focus underline" href="/">home</a> or explore care options.`,
          buttonValue: 'Find care',
          buttonHref: this.getSitterSearchUrl(careType),
        };
      }
      if (careType === 'housekeeping') {
        return {
          title: 'This profile is unavailable',
          body: `${baseMessage} Go to our <a class="text-primary primary-focus underline" href="/">homepage</a> or log in to find care today.`,
          buttonValue: 'Log in',
          buttonHref: `/login?return_path=${encodeURIComponent(this.$route.fullPath)}`,
        };
      }
      return {
        title: 'This profile is unavailable',
        body: `${baseMessage} You can <a class="text-primary primary-focus underline" href="${this.getLandingPageUrl(careType)}">learn more</a> about how care works on Sittercity or log in to find care now.`,
        buttonValue: 'Log in',
        buttonHref: `/login?return_path=${encodeURIComponent(this.$route.fullPath)}`,
      };
    },
  },
  methods: {
    getSitterSearchUrl(careType: string): string {
      const zipcode = this.viewerAccountData?.location?.zipcode || '';
      const isChildCare = careType === 'babysitter' || careType === 'nanny';
      return isChildCare
        ? `/availability/search?careType=${careType}&zipcode=${zipcode}`
        : `/profile/provider/search?search_strategy=${careType}&zipcode=${zipcode}`;
    },
    getLandingPageUrl(careType: string): string {
      const careTypeRoutes: Record<string, string> = {
        babysitter: '/babysitters',
        nanny: '/nanny',
        pet_care: '/pet-sitting',
        companion_care: '/companion-care',
      };
      return careTypeRoutes[careType] || '/babysitters';
    },
    getNewProfileUrl(type: string): string {
      const route = getNewProfileRoute(type);
      const params = new URLSearchParams();
      params.append('additionalProfile', route.query.additionalProfile);
      params.append('careType', route.query.careType);
      return `${route.path}?${params.toString()}`;
    },
    getJobSearchUrl(type: string): string {
      return `/provider/jobs/search?careType=${type}`;
    },
  },
};
</script>
